import {useEffect, useRef} from 'react';
import {useGTMDispatch} from '@elgorditosalsero/react-gtm-hook';
import {format} from 'date-fns';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {useUserContext} from 'shared/contexts/UserContext/useUserContext';
import isPatient from 'shared/utils/isPatient';
import useIsMobile from './useIsMobile';
import useQueryParams from './useQueryParams';

export default function usePushPageViewToDataLayer(): void {
  const getLastItem = (p: string) => p.substring(p.lastIndexOf('/') + 1);
  const location = useLocation();
  const searchParams = useQueryParams();
  const sendDataToGTM = useGTMDispatch();
  const isMobile = useIsMobile();
  const {i18n} = useTranslation();
  const {user} = useUserContext();
  const path = useRef([getLastItem(location.pathname)]);
  useEffect(
    function() {
      path.current.push(getLastItem(location.pathname));

      let randomSessionId = window.localStorage.getItem('BELLA_AURORA_RANDOM_SESSION_ID');
      if (!randomSessionId) {
        randomSessionId =
          new Date().getTime() +
          '.' +
          Math.random()
            .toString(36)
            .substring(5);
        window.localStorage.setItem('BELLA_AURORA_RANDOM_SESSION_ID', randomSessionId);
      }

      let userType = 'no_interesado';
      if (user) {
        userType = 'interesado';
        if (isPatient(user)) {
          if (user.totalTests === 1) {
            userType = 'muy_interesado';
          } else if (user.totalTests > 1) {
            userType = 'recurrente';
          }
        }
      }

      const now = new Date();

      const getAll = window.ga?.getAll;
      if (typeof getAll !== 'function') {
        return;
      }

      sendDataToGTM({
        event: 'pageView',
        value: {
          category: {
            originLink: path.current.join('.'),
            contentSection: location.pathname.slice(1).replaceAll('/', '_'),
            url: location.pathname
          },
          context: {
            platform: isMobile ? 'webmobile' : 'webmobile',
            codeVersion: '2.0',
            language: i18n.language,
            countryId: i18n.language
          },
          userHit: {
            visitorType: userType,
            userId: user?.id ?? null,
            gaClientId: getAll()[0].get('clientId'),
            userStatus: user ? 'login' : 'no_login',
            isNew: searchParams.get('confirmed') === 'true' ? 1 : 0,
            isSignup: window.localStorage.getItem('BELLA_AURORA_IS_SIGNUP'),
            timeStamp: format(now, 'yyyy-MM-dd HH:mm:ss'),
            randomSessionId: randomSessionId,
            uploaded_photos: user ? (isPatient(user) ? user.totalTests : 0) : 0,
            navigation: ''
          }
        }
      });
    },
    [sendDataToGTM, location, i18n.language, user, isMobile, searchParams]
  );

  useEffect(function() {
    window.addEventListener('beforeunload', function(e) {
      window.localStorage.setItem('BELLA_AURORA_IS_SIGNUP', '0');
    });
  }, []);
}
